import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentCompanyId } from '../lib/company';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DollarSign, AlertCircle, TrendingUp, Users, CreditCard, FileText, Search, MessageCircle } from 'lucide-react';
import DocumentActions from '../components/ui/document-actions';
import { toast } from 'sonner';

export default function Finance() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(getCurrentCompanyId() || '');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [paymentDialog, setPaymentDialog] = useState(null); // { saleId, max }
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState({ id: '', name: '' });
  const [paymentReference, setPaymentReference] = useState('');
  const [clientSales, setClientSales] = useState({});
  const receivablesRef = useRef(null);

  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const isAdmin = user?.role_id === 'admin';
  const isVendedor = user?.role_id === 'vendedor';

  const scrollToReceivables = () => {
    receivablesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sendWhatsappReminder = (r) => {
    const phone = (r.phone || '').replace(/[^0-9]/g, '');
    const msg = `SalesPRO: Hola ${r.client_name}, le recordamos amablemente que tiene un saldo pendiente de $${r.pending.toFixed(2)} con nosotros. Quedamos atentos para coordinar su pago. ¡Gracias!`;
    const text = encodeURIComponent(msg);
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    if (!phone) toast.info('SalesPRO: El cliente no tiene teléfono registrado; se abrirá WhatsApp para elegir el contacto.');
    window.open(url, '_blank');
  };

  useEffect(() => { init(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedCompany !== null) loadSummary(); }, [selectedCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  const init = async () => {
    try {
      const [compsRes, methodsRes] = await Promise.all([
        api.getCompanies(),
        api.getPaymentMethods().catch(() => ({ data: [] })),
      ]);
      setCompanies(compsRes.data);
      setPaymentMethods(methodsRes.data || []);
      await loadSummary();
    } catch (e) {
      toast.error('SalesPRO: Error al cargar finanzas');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await api.getFinanceSummary(selectedCompany || undefined);
      setSummary(res.data);
    } catch (e) {
      toast.error('SalesPRO: Error al cargar resumen financiero');
    }
  };

  const loadClientSales = async (clientId) => {
    try {
      const res = await api.getSales({ client_id: clientId });
      setClientSales({ ...clientSales, [clientId]: res.data });
    } catch (e) { /* ignore */ }
  };

  const registerPayment = async () => {
    if (!paymentDialog || !paymentAmount) return;
    const amt = parseFloat(paymentAmount);
    if (isNaN(amt) || amt <= 0) { toast.error('SalesPRO: Monto inválido'); return; }
    try {
      await api.registerPayment(paymentDialog.saleId, amt, {
        method_id: paymentMethod.id || undefined,
        method_name: paymentMethod.name || undefined,
        reference: paymentReference || undefined,
      });
      toast.success('SalesPRO: Pago registrado');
      setPaymentDialog(null);
      setPaymentAmount('');
      setPaymentMethod({ id: '', name: '' });
      setPaymentReference('');
      loadSummary();
      // Refrescar ventas del cliente si está expandido
      if (paymentDialog.clientId) loadClientSales(paymentDialog.clientId);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'SalesPRO: Error al registrar pago');
    }
  };

  if (loading || !summary) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  const filteredReceivables = summary.receivables.filter(r =>
    !search || r.client_name.toLowerCase().includes(search.toLowerCase()) || (r.cedula_rif || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="finance-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Módulo Financiero</h1>
          <p className="text-sm text-[#64748B]">Cuentas por cobrar y estado de pagos</p>
        </div>
        {(isMaster || isAdmin) && companies.length > 1 && (
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
            data-testid="finance-company-select"
          >
            <option value="">Todas las empresas activas</option>
            {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
        )}
      </div>

      {/* Exportar / compartir resumen financiero (PDF de marca) */}
      {(isMaster || isAdmin) && (
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs text-[#64748B] mb-1.5 font-medium uppercase tracking-wide">Exportar / compartir cuentas por cobrar (PDF de marca)</p>
            <DocumentActions
              fetchBlob={() => api.getBrandedReportPdf({ type: 'finance', company_id: selectedCompany || undefined })}
              filename={`SalesPRO_finanzas_${selectedCompany || 'consolidado'}.pdf`}
              title={`SalesPRO — Cuentas por Cobrar · Por cobrar $${summary.totals.pending.toFixed(2)} · ${summary.all_clients_with_debt} deudores`}
            />
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border border-[#E2E8F0] bg-gradient-to-br from-[#004D40] to-[#00796B] text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-white/70 text-xs uppercase mb-1"><DollarSign size={12} />Facturado</div>
            <p className="text-xl sm:text-2xl font-bold">${summary.totals.invoiced.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-700 text-xs uppercase mb-1"><CreditCard size={12} />Cobrado</div>
            <p className="text-xl sm:text-2xl font-bold text-green-700">${summary.totals.paid.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] bg-red-50 cursor-pointer hover:ring-2 hover:ring-red-300 transition-shadow" onClick={scrollToReceivables} data-testid="finance-cta-pending">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-700 text-xs uppercase mb-1"><AlertCircle size={12} />Por cobrar</div>
            <p className="text-xl sm:text-2xl font-bold text-red-700">${summary.totals.pending.toFixed(2)}</p>
            <p className="text-[10px] text-red-600/70 mt-0.5">Ver detalle ↓</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow" onClick={scrollToReceivables} data-testid="finance-cta-debtors">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase mb-1"><Users size={12} />Deudores</div>
            <p className="text-xl sm:text-2xl font-bold">{summary.all_clients_with_debt}</p>
            <p className="text-[10px] text-[#94A3B8] mt-0.5">Ver detalle ↓</p>
          </CardContent>
        </Card>
      </div>

      {/* Resumen de facturas */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-3 text-center">
            <FileText size={20} className="mx-auto text-[#64748B] mb-1" />
            <p className="text-2xl font-bold">{summary.totals.invoices_count}</p>
            <p className="text-[10px] text-[#64748B] uppercase">Total facturas</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-3 text-center">
            <AlertCircle size={20} className="mx-auto text-orange-600 mb-1" />
            <p className="text-2xl font-bold text-orange-700">{summary.totals.pending_invoices_count}</p>
            <p className="text-[10px] text-[#64748B] uppercase">Pendientes</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-3 text-center">
            <TrendingUp size={20} className="mx-auto text-green-600 mb-1" />
            <p className="text-2xl font-bold text-green-700">{summary.totals.delivered_count}</p>
            <p className="text-[10px] text-[#64748B] uppercase">Entregadas</p>
          </CardContent>
        </Card>
      </div>

      {/* Cuentas por cobrar */}
      <Card className="border border-[#E2E8F0]" ref={receivablesRef}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
            <h2 className="font-heading font-bold text-lg">Cuentas por cobrar</h2>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input
                placeholder="Buscar cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 w-full sm:w-64"
                data-testid="finance-search"
              />
            </div>
          </div>
          {filteredReceivables.length === 0 ? (
            <p className="text-center text-[#94A3B8] py-8">No hay cuentas por cobrar 🎉</p>
          ) : (
            <div className="space-y-2">
              {filteredReceivables.map(r => (
                <div key={r.client_id} className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`receivable-${r.client_id}`}>
                  <div
                    className="p-3 cursor-pointer hover:bg-[#F8F9FA]"
                    onClick={() => !clientSales[r.client_id] && loadClientSales(r.client_id)}
                  >
                    <div className="flex justify-between items-start gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{r.client_name}</p>
                        <p className="text-xs text-[#64748B] font-mono">{r.cedula_rif}</p>
                        <p className="text-xs text-[#64748B] mt-1">{r.open} factura(s) pendientes de {r.count} totales</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-red-600">${r.pending.toFixed(2)}</p>
                        <p className="text-[10px] text-[#64748B]">de ${r.invoiced.toFixed(2)}</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); sendWhatsappReminder(r); }}
                          className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-green-700 border border-green-200 bg-green-50 hover:bg-green-100 transition-colors"
                          data-testid={`whatsapp-reminder-${r.client_id}`}
                          title="Enviar recordatorio por WhatsApp"
                        >
                          <MessageCircle size={13} /> Recordar
                        </button>
                      </div>
                    </div>
                  </div>
                  {clientSales[r.client_id] && (
                    <div className="px-3 pb-3 pt-1 bg-[#F8F9FA] border-t">
                      <table className="w-full text-xs">
                        <thead className="text-[#64748B] uppercase text-[10px]">
                          <tr>
                            <th className="text-left p-1">Factura</th>
                            <th className="text-right p-1">Total</th>
                            <th className="text-right p-1">Pagado</th>
                            <th className="text-right p-1">Saldo</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {clientSales[r.client_id].filter(s => s.payment_status !== 'pagado').map(s => (
                            <tr key={s.sale_id} className="border-t">
                              <td className="p-1 font-mono">{s.invoice_number || s.sale_id.slice(0, 8)}</td>
                              <td className="p-1 text-right">${s.total.toFixed(2)}</td>
                              <td className="p-1 text-right">${(s.amount_paid || 0).toFixed(2)}</td>
                              <td className="p-1 text-right font-bold text-red-600">${(s.total - (s.amount_paid || 0)).toFixed(2)}</td>
                              <td className="p-1 text-right">
                                {(isMaster || isAdmin) && (
                                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={(e) => {
                                    e.stopPropagation();
                                    setPaymentDialog({ saleId: s.sale_id, clientId: r.client_id, max: s.total - (s.amount_paid || 0), invoice: s.invoice_number });
                                    setPaymentAmount('');
                                  }} data-testid={`pay-${s.sale_id}`}>
                                    Pagar
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de pago */}
      {paymentDialog && (
        <Dialog open={!!paymentDialog} onOpenChange={() => setPaymentDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Registrar pago</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-[#64748B]">Factura: <b>{paymentDialog.invoice}</b></p>
              <p className="text-sm">Saldo pendiente: <b className="text-red-600">${paymentDialog.max.toFixed(2)}</b></p>
              <Input
                type="number"
                step="0.01"
                placeholder="Monto del pago"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="h-11"
                data-testid="payment-amount-input"
                autoFocus
              />
              <select
                value={paymentMethod.id}
                onChange={(e) => {
                  const m = paymentMethods.find(pm => pm.method_id === e.target.value);
                  setPaymentMethod({ id: e.target.value, name: m?.name || '' });
                }}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white h-11"
                data-testid="payment-method-select"
              >
                <option value="">Método de pago...</option>
                {paymentMethods.map(m => <option key={m.method_id} value={m.method_id}>{m.name}</option>)}
              </select>
              <Input
                placeholder="Referencia (opcional)"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                className="h-11"
                data-testid="payment-reference-input"
              />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setPaymentAmount(paymentDialog.max.toFixed(2))}>
                  Pagar todo
                </Button>
                <Button className="flex-1" onClick={registerPayment} data-testid="confirm-payment">
                  Registrar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
